<template lang="pug">
div.grid.grid-cols-1.gap-1
  template(v-if="loading")
    div.rounded.border.border-border.p-3.text-sm.text-muted-foreground(class="bg-muted/40")
      | 备注加载中...
  template(v-else-if="errorMessage")
    div.rounded.p-3.text-sm.text-destructive(class="border border-destructive/30 bg-destructive/5")
      p.font-medium 备注加载失败
      p.mt-1.whitespace-pre-line.break-words {{ errorMessage }}
  template(v-else)
    .flex.text-left.w-auto.items-top.gap-1.text-sm.rounded.min-w-0(class="hover:bg-secondary hover:text-bold" v-for="note of thisServiceNotes")
      div.min-w-16
        TooltipProvider(:delayDuration="Number(100)")
          Tooltip
            TooltipTrigger.text-left.text-xs
              span.text-xs.text-left {{ $utils.dateStrShort(note.createdAt, false) }}
            TooltipContent
              span {{ $utils.dateStr(note.createdAt) + ' | #' + note.addedBy }}
      div.flex-1.min-w-0
        p.text.whitespace-pre-line.break-words {{ note.text }}
      div.items-center.inline-flex
        p.del-icon
          Icon(name="majesticons:trash-line" class="ml-1 clickable" @click="delNote(note)")
  .new-note.mt-1
    Textarea.border-0(v-model="newNote.text" @keydown="addNote"  :placeholder="noteInputPlaceholder" :disabled="noteInputDisabled" type="textarea" class="bg-[var(--background-color-pure)] text-[16px]")
</template>

<script lang="ts" setup>
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { useVisitorSession } from '~/stores/useVisitorSession'
import { toast } from '~/utils/sonner'
const emit = defineEmits([
  // 'newNote',
  // 'delNote',
  'update:notes'
])
const prop = defineProps<{
  notes: Array<Note> | undefined | null,
  serviceId?: number,
  userId: number,
  relatedMatter?: string,
  loading?: boolean,
  error?: unknown,
}>()
const session = useVisitorSession()
const $utils = useUtils()
const delNoteLoading = ref({} as Record<string, boolean>)
const loading = computed(() => Boolean(prop.loading))
const errorMessage = computed(() => {
  const error = prop.error as any
  if (!error) {
    return ''
  }
  return error?.data?.message
    || error?.data?.error
    || error?.statusMessage
    || error?.message
    || String(error)
})
const noteInputDisabled = computed(() => adding.value || loading.value || Boolean(errorMessage.value))
const noteInputPlaceholder = computed(() => {
  if (loading.value) {
    return '备注加载中，暂时无法输入'
  }
  if (errorMessage.value) {
    return '备注加载失败，暂时无法输入新备注'
  }
  return '输入备注，「Command + Enter」 或「连按3次Enter」提交'
})

const thisServiceNotes = computed({
  get() {
    return prop.notes?.filter?.(note => note.userId === prop.userId && (!note.serviceId || !prop.serviceId || note.serviceId === prop.serviceId)) || []
  },
  set(val) {
    emit('update:notes', val)
  }
})
const newNote = ref({
  text: '',
} as Note)
const adding = ref(false)
let enterPressedTime = 0
async function addNote(evt) {
  if (noteInputDisabled.value) {
    return
  }
  if (evt.keyCode !== 13) {
    enterPressedTime = 0
    return
  }
  if (!evt.metaKey) {
    enterPressedTime += 1
    if (enterPressedTime < 3) {
      return
    } else {
      enterPressedTime = 0
    }
  }
  await doAddNote()
}
async function doAddNote(content?: string) {
  if (noteInputDisabled.value) {
    return
  }
  console.log('addNote~~~')
  adding.value = true
  newNote.value.text = content || newNote.value.text.trim()
  const data = {
    ...newNote.value,
    addedBy: session.id,
    relatedMatter: prop.relatedMatter,
    serviceId: prop.serviceId,
    userId: prop.userId,
  }

  try {
    if (data.text.length) {
      const addedNote = await useAdmin.addTrAbuseNote(data)
      // emit('newNote', addedNote.value)
      console.log('addedNote', data)
      toast({ title: '已保存记录', description: content });
      newNote.value.text = ''
      thisServiceNotes.value?.push(addedNote)
      // emit('update:notes', [...(thisServiceNotes.value || []), addedNote.value])
    }
  } catch (e) {
    toast({ title: '记录失败', description: (e as any).message || e as string, variant: 'destructive' });
  }
  adding.value = false
}
async function delNote(note) {
  if (delNoteLoading[note.objectId]) {
    return
  }
  const confirmed = await useDialog().confirm({
    title: '确定删除？',
    description: h('p', {
      class: 'text-xs text-gray-500 text-xs',
    }, `ID: ${note.objectId}\nCreated: ${$utils.dateStr(note.createdAt)}`),
    body: h('p', [
      note.text,
    ]),
    // confirmButtonText: '删除',
    // cancelButtonText: '取消',
    defaultFocusButton: 'confirm',
  })
  console.log('delNote confirmed?', confirmed)
  if (confirmed !== 'confirm') {
    return
  }
  delNoteLoading[note.objectId] = true
  await useAdmin.delTrAbuseNote({
    noteId: note.objectId,
  })
  thisServiceNotes.value = thisServiceNotes.value?.filter(n => n.objectId !== note.objectId)
  toast({ title: '已删除', description: note.text });
  console.log('Note deleted:', note.objectId)
  delNoteLoading[note.objectId] = false
}

function setNoteContent(content) {
  newNote.value.text = content
}
defineExpose({
  setNoteContent,
  doAddNote,
})
</script>

<style lang="postcss" scoped>
.note-history {
  text-align: left;

  &:hover {
    text-shadow: 0px 0px 0.2px var(--primary-color);
    transition: 0.1s ease-out;
  }

  p {
    display: flex;
    align-items: center;
    justify-self: flex-start;
    color: var(--primary-color);

    &.text {
      color: var(--primary-color);
      font-size: 0.8rem;
      white-space: pre-line;
    }

    &.date {
      font-size: 0.65rem;
      color: var(--secondary-color);
      margin-right: 1rem;
      font-weight: bold;
    }

    &.del-icon {
      font-size: 1rem;
      color: var(--primary-color);
    }
  }
}
</style>
